import { Nullable } from '../../types/common'
import { parseHTML } from '../../utils/dom'
import { delay, $el } from '../../utils/utils'
import { RecentlyViewed } from '../recently-viewed/recently-viewed'
import { SidebarComponent } from '../../components/sidebar/sidebar'
import { CarouselComponent } from 'src/scripts/components/carousel'
import { isMobile } from 'src/scripts/utils/check-media'
import { throttle } from '../../utils/debounce'
import { VariantPicker } from './product-variant-picker'

const { Shopify }: any = window
const SELECTOR_QUICK_VIEW_MEDIA_CAROUSEL = '.quick-view__media-carousel'
const SELECTOR_QUICK_VIEW_MEDIA = '.quick-view__media'
const SELECTOR_QUICK_PRODUCT_CONTENT = '.product__content'
const SELECTOR_VARIANT_PICKER = 'variant-picker'
const SELECTOR_PRODUCT_INFO = '.product__info-wrapper'
const SELECTOR_QUICK_VIEW_SCROLL_CONTENT = '[data-quick-view-scroll-content]'
const ATTRIBUTE_FOOTER_SHADOW_VISIBLE = 'footer-shadow-visible'
const ATTRIBUTE_TOP_SHADOW_VISIBLE = 'top-shadow-visible'
const SCROLL_TRESHOLD = 10

export class QuickView extends SidebarComponent {
  cache: object = {}
  rendered: Nullable<HTMLElement>
  scrollContent: Nullable<HTMLElement>
  productContent: Nullable<HTMLElement>
  mediaContent: Nullable<HTMLElement>
  resizeObserver: ResizeObserver

  mount(): void {
    super.mount()
    this.scrollContent = $el(SELECTOR_QUICK_VIEW_SCROLL_CONTENT, this.element)
    this.mediaContent = $el(SELECTOR_QUICK_VIEW_MEDIA, this.element)
    this.productContent = $el(SELECTOR_QUICK_PRODUCT_CONTENT, this.element)
    this.resizeObserver = new ResizeObserver(this.handleResize)
    this.resizeObserver.observe(this.scrollContent)
    this.addListener(this.scrollContent, 'scroll', this.handleScroll)
    this.addListener(this.productContent, 'scroll', this.handleProductScroll)
  }

  unmount(): void {
    super.unmount()
    this.resizeObserver.disconnect()
    this.removeListener(this.scrollContent, 'scroll', this.handleScroll)
    this.removeListener(this.productContent, 'scroll', this.handleProductScroll)
    this.toggleAttribute(ATTRIBUTE_TOP_SHADOW_VISIBLE, false)
  }

  handleResize = throttle(() => {
    this.setFooterShadow()
    this.setTopShadow()
  }, 200)

  handleScroll = () => this.setFooterShadow()

  handleProductScroll = () => this.setTopShadow()

  handleSectionSelect = async ({ detail: { sectionId } }) => {
    if (this.isSection && this.dataset.sectionId === sectionId) {
      const url = this.dataset.defaultProductUrl
      const id = this.dataset.defaultProductId

      if (!url || !id) {
        return
      }

      this.toggleOverlay(true)
      this.requestProductFromUrl(url, id, true)
      await this.openAndRenderProductByUrl(url)
    }
  }

  handleSectionDeselect = ({ detail: { sectionId } }): void => {
    if (this.isSection && this.dataset.sectionId === sectionId) {
      this.hide()
    }
  }

  getFetchUrl = (url: string) => {
    const questionMarkIndex = url.indexOf('?')

    if (questionMarkIndex !== -1) {
      return (
        url.slice(0, questionMarkIndex) +
        '/gsc-quick-view' +
        url.slice(questionMarkIndex)
      )
    }

    return url + '/gsc-quick-view'
  }

  requestProductFromUrl(url: string, id: string, disableCache = false): void {
    const fetchUrl = this.getFetchUrl(url)

    if (disableCache) {
      this.cache[url] = this.fetchProduct(fetchUrl)
      return
    }

    if (!this.cache[url]) {
      this.rendered = $el(
        `.shopify-section [data-quick-view-product-id="${id}"]`,
      )

      if (this.rendered && this.rendered.dataset.quickViewProductUrl === url) {
        this.cache[url] = delay(0).then(() => this.rendered)
      } else {
        this.cache[url] = this.fetchProduct(fetchUrl)
      }
    }
  }

  fetchProduct = (fetchUrl: string) =>
    fetch(fetchUrl)
      .then(response => response.text())
      .then(responseText => parseHTML(responseText))
      .catch(() => console.log('error in quick view'))

  setFooterShadow = () => {
    const { scrollTop, scrollHeight, clientHeight } = this.scrollContent

    this.toggleAttribute(
      ATTRIBUTE_FOOTER_SHADOW_VISIBLE,
      scrollHeight > clientHeight &&
        scrollHeight - Math.floor(scrollTop) > clientHeight,
    )
  }

  setTopShadow = () => {
    if (isMobile()) {
      const { scrollTop: productScrollTop } = this.productContent
      const { clientHeight: mediaClientHeight } = this.mediaContent

      this.toggleAttribute(
        ATTRIBUTE_TOP_SHADOW_VISIBLE,
        productScrollTop > mediaClientHeight - SCROLL_TRESHOLD,
      )
    }
  }

  openAndRenderProductByUrl = async (url: string): Promise<void> => {
    if (this.cache[url]) {
      const html = await this.cache[url]

      if (this.rendered) {
        this.rendered.replaceChildren()
      }

      if (this.isOpen) {
        await this.hide()
      }

      this.setProductFromHTML(html)
      Shopify?.PaymentButton?.init()

      await this.open()

      if (this.dataset.viewType == 'drawer') {
        this.reInitCarousel()
      } else {
        Promise.all(
          this.getAnimations({ subtree: true }).map(
            animation => animation.finished,
          ),
        ).then(() => this.reInitCarousel())
      }
    }
  }

  setProductFromHTML(html: Document): void {
    if (!this.dataset.targetId) return

    const target = $el(`#${this.dataset.targetId}`, html)
    if (!target) return

    RecentlyViewed.updateProductsInLocalStorage(target.dataset.productHandle)

    const targetClass = target.getAttribute('class') || ''
    const clonedTarget = target.cloneNode(true)

    this.element.setAttribute('class', targetClass)

    if (this.isOpen) {
      this.updateElements(clonedTarget)
    } else {
      this.element.replaceChildren(...clonedTarget.childNodes)
    }
  }

  updateElements = clonedTarget => {
    const currentVariantPicker = $el(SELECTOR_VARIANT_PICKER, this.element)
    const newVariantPicker = $el(SELECTOR_VARIANT_PICKER, clonedTarget)

    $el(SELECTOR_PRODUCT_INFO, this.element).replaceWith(
      $el(SELECTOR_PRODUCT_INFO, clonedTarget),
    )

    if (currentVariantPicker?.dataset.url !== newVariantPicker.dataset.url) {
      $el(SELECTOR_QUICK_VIEW_MEDIA, this.element).replaceWith(
        $el(SELECTOR_QUICK_VIEW_MEDIA, clonedTarget),
      )
      this.reInitCarousel()
    }
  }

  reInitCarousel() {
    const mediaCarousel = this.querySelector(
      SELECTOR_QUICK_VIEW_MEDIA_CAROUSEL,
    ) as CarouselComponent

    if (this.dataset.viewType == 'drawer') {
      mediaCarousel.setAttribute('data-axis', isMobile() ? 'x' : 'y')
      mediaCarousel.reInit({
        axis: 'y',
        breakpoints: {
          '(max-width: 768px)': { axis: 'x', dragFree: false },
          '(min-width: 768px)': { axis: 'y', dragFree: true },
        },
      })
    } else {
      mediaCarousel.reInit({
        axis: 'x',
      })
    }

    const variantPicker: VariantPicker = $el(SELECTOR_VARIANT_PICKER, this)
    variantPicker?.setFirstMediaByVariant()
  }
}
